import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { LoadingState, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import JobBuilder from "@/pages/recruiter/JobBuilder";
import TestStudio from "@/pages/recruiter/TestStudio";
import { PublishStep } from "@/components/job/PublishStep";
import { ScreeningStep } from "@/components/job/ScreeningStep";
import { StageStep } from "@/components/job/StageStep";
import { STEPS, SECTIONS, sectionPath } from "@/components/job/Lifecycle";
import { cn } from "@/lib/utils";
import { EMBEDDED } from "@/lib/embedded";
import { Check, Link2, Loader2, Briefcase, MessageSquare } from "lucide-react";

const ALIASES = { funnel: "screening", role: "build", assessment: "test" };

function statusPill(job) {
  const lc = job.lifecycle || {};
  if (job.status === "published") return { text: `Live${lc.day != null ? ` · day ${lc.day}` : ""}`, tone: "green" };
  if (lc.signed_off) return { text: "Signed off · ready", tone: "green" };
  return { text: "Draft", tone: "muted" };
}
const PILL = { green: "bg-primary/12 text-primary", muted: "bg-foreground/[0.07] text-muted-foreground", amber: "bg-amber-400/12 text-amber-700 dark:text-amber-300" };

// The header is navigation only; each step's page owns its own actions, so nothing appears twice.
function JobHeader({ job, section }) {
  const nav = useNavigate();
  const lc = job.lifecycle || { step: 1, counts: {} };
  const pill = statusPill(job);
  const live = job.status === "published";
  const meta = { Screening: lc.to_review ? `${lc.to_review} new` : "", Interviews: lc.counts?.interview ? String(lc.counts.interview) : "", Offer: lc.counts?.offer ? String(lc.counts.offer) : "" };
  const copy = async () => { try { await navigator.clipboard.writeText(`${window.location.origin}/careers/${job.id}`); toast.success("Public link copied"); } catch { toast.message("Public link", { description: `${window.location.origin}/careers/${job.id}` }); } };
  const [opening, setOpening] = useState(false);
  const openChat = async () => {
    setOpening(true);
    try { const { data } = await api.post(`/ai/job-sessions/${job.id}`); nav(`/copilot/${data.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not open the conversation"); setOpening(false); }
  };
  return (
    <div className="border-b border-border/70 shrink-0" data-testid="job-header">
      <div className="app-container pt-4 short:pt-3 space-y-3 short:space-y-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <nav className="text-sm text-muted-foreground shrink-0" aria-label="Breadcrumb"><Link to="/recruiter/jobs" className="hover:text-foreground" data-testid="job-breadcrumb-jobs">Jobs</Link> <span aria-hidden>›</span></nav>
            <h1 className="min-w-0 text-xl sm:text-2xl font-display font-semibold tracking-tight [overflow-wrap:anywhere] md:truncate" title={job.title} data-testid="job-title">{job.title === "Untitled role" ? "New role" : job.title}</h1>
            <span className={cn("shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium", PILL[pill.tone])} data-testid="job-status-pill">{pill.text}</span>
            {job.is_sample && <span className={cn("shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium", PILL.amber)} data-testid="job-sample-pill">Sample data</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={openChat} disabled={opening} className="gap-2" data-testid="job-open-chat">{opening ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />} Open in chat</Button>
            {live && <Button variant="outline" size="sm" onClick={copy} className="gap-2" data-testid="job-copy-link"><Link2 className="h-4 w-4" /> Copy link</Button>}
          </div>
        </div>
        <div className="flex overflow-x-auto no-scrollbar -mb-px" role="tablist" aria-label="Hiring steps" data-testid="job-steps">
          {STEPS.map((label, i) => {
            const n = i + 1;
            const key = SECTIONS[i];
            const current = section === key;
            const done = n < lc.step || (live && n <= 3);
            return (
              <button key={label} role="tab" aria-selected={current} onClick={() => nav(sectionPath(job.id, key))} data-testid={`job-step-${key}`} data-done={done}
                className={cn("flex items-center gap-2.5 pr-5 mr-1 pt-1 pb-3 short:pb-2.5 border-b-2 text-sm whitespace-nowrap transition-colors",
                  current ? "border-primary text-foreground font-semibold" : "border-transparent font-medium hover:text-foreground",
                  !current && (done ? "text-muted-foreground" : n === lc.step ? "text-foreground/80" : "text-muted-foreground/70"))}>
                <span className={cn("h-[22px] w-[22px] rounded-full flex items-center justify-center text-xs font-bold border",
                  current ? "bg-primary text-primary-foreground border-transparent" : done ? "bg-primary/15 text-primary border-transparent" : "border-foreground/20")}>
                  {done && !current ? <Check className="h-3 w-3" /> : n}
                </span>
                {label}
                {meta[label] && <span className="text-xs text-muted-foreground font-normal">{meta[label]}</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function Workspace() {
  const { jobId, section: raw } = useParams();
  const nav = useNavigate();
  const section = ALIASES[raw] || raw;
  const [job, setJob] = useState(null);
  const [missing, setMissing] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(() => api.get(`/jobs/${jobId}`).then((r) => setJob(r.data)).catch(() => setMissing(true)), [jobId]);
  useEffect(() => { load(); }, [load, section]);
  useEffect(() => {
    if (job && !SECTIONS.includes(section)) nav(sectionPath(jobId, job.lifecycle?.next?.section || "build"), { replace: true });
  }, [job, section, jobId, nav]);

  const publish = async () => {
    setPublishing(true);
    try {
      const lc = job.lifecycle || {};
      // One publish moment: the reviewed assessment version goes live with the role.
      if (lc.latest_version && !lc.active_version) await api.post(`/jobs/${jobId}/test/${lc.latest_version}/publish`);
      await api.post(`/jobs/${jobId}/publish`); toast.success("Role published to your careers page"); await load(); nav(sectionPath(jobId, "screening"));
    }
    catch (e) { toast.error(e.response?.data?.detail || "Could not publish"); }
    finally { setPublishing(false); }
  };

  if (missing) return <div className="recruiter-page"><EmptyState icon={Briefcase} title="Role not found" description="It may have been deleted, or it belongs to another organization." action={<Button className="mt-2" onClick={() => nav("/recruiter/jobs")} data-testid="workspace-missing-back">Back to jobs</Button>} /></div>;
  if (!job || !SECTIONS.includes(section)) return <LoadingState label="Opening role…" />;

  return (
    <div className={cn("job-workspace", EMBEDDED && "is-embedded")} data-testid="job-workspace" data-section={section}>
      {!EMBEDDED && <JobHeader job={job} section={section} />}
      <div className="job-workspace-body" key={section} data-testid="job-workspace-body">
        {section === "build" && <JobBuilder onChanged={load} />}
        {section === "test" && <TestStudio onChanged={load} />}
        {section === "publish" && <PublishStep job={job} onPublish={publish} publishing={publishing} />}
        {section === "screening" && <ScreeningStep job={job} />}
        {section === "interviews" && <StageStep job={job} kind="interviews" />}
        {section === "offer" && <StageStep job={job} kind="offer" />}
      </div>
    </div>
  );
}
